import { useEffect, useState } from 'react';
import api from '../services/api';
import { dueDateToQuarter } from '../utils/dateHelpers';
import { getQuarterlyPayments } from '../services/quarterlyPayments';
import { getSavingsSummary } from '../services/savings';
import { INSIGHT_TYPES } from '../constants/insightTypes';

// Shared dashboard/detail-screen data layer. Mirrors the exact fetch, staged
// paid-quarters lookup, and refetch handlers that Dashboard.js performs inline,
// so the detail screens (Taxes / Cash flow / Money) can render the same card
// components against the same data without duplicating that logic per screen.
//
// NOTE: Dashboard.js still has its own inline copy of this for now (this step is
// additive and must not change Dashboard). A follow-up should switch Dashboard +
// the new Home onto this hook so the two copies collapse into one. See the
// hand-off note in the PR/summary.
export default function useTruflowData() {
  const [loading, setLoading]                   = useState(true);
  const [taxData, setTaxData]                   = useState(null);
  const [expensesData, setExpensesData]         = useState(null);
  const [curveData, setCurveData]               = useState(null);
  const [cashflowData, setCashflowData]         = useState(null);
  const [paidQuartersData, setPaidQuartersData] = useState(null);
  const [taxSetupData, setTaxSetupData]         = useState(null);
  const [insightsData, setInsightsData]         = useState([]);
  const [savingsData, setSavingsData]           = useState(null);
  const [savingsModalPool, setSavingsModalPool] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      // Stage 1: parallel fetch of all core data (same set Dashboard fetches).
      const [tax, expenses, curve, cashflow, setup, insightsRes, savingsRes] = await Promise.allSettled([
        api.get('/tax/calculate'),
        api.get('/expenses'),
        api.get('/income/curve'),
        api.get('/cashflow/projection'),
        api.get('/tax/setup'),
        api.get('/insights'),
        api.get('/savings/summary'),
      ]);

      if (cancelled) return;
      const resolvedTaxData = tax.status === 'fulfilled' ? tax.value.data : null;
      setTaxData(resolvedTaxData);
      setExpensesData(expenses.status === 'fulfilled' ? expenses.value.data : null);
      setCurveData(curve.status === 'fulfilled' ? curve.value.data : null);
      setCashflowData(cashflow.status === 'fulfilled' ? cashflow.value.data : null);
      setTaxSetupData(setup.status === 'fulfilled' ? setup.value.data : null);
      setInsightsData(insightsRes.status === 'fulfilled' ? (insightsRes.value.data?.insights ?? []) : []);
      setSavingsData(savingsRes.status === 'fulfilled' ? savingsRes.value.data : null);
      setLoading(false);

      // Stage 2: fetch paid quarters once we know the tax year from the due date.
      const dueDate = resolvedTaxData?.next_due_date ?? resolvedTaxData?.due_date ?? null;
      const qi = dueDateToQuarter(dueDate);
      if (qi) {
        try {
          const payments = await getQuarterlyPayments(qi.tax_year);
          if (!cancelled) setPaidQuartersData(payments);
        } catch {}
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  // Insight bodies include savings funding progress, so refetch both together.
  async function refetchSavingsAndInsights() {
    const [summary, ins] = await Promise.allSettled([
      getSavingsSummary(),
      api.get('/insights'),
    ]);
    if (summary.status === 'fulfilled') setSavingsData(summary.value);
    if (ins.status === 'fulfilled') setInsightsData(ins.value.data?.insights ?? []);
  }

  async function refetchTaxAndPayments() {
    try {
      const taxRes = await api.get('/tax/calculate');
      setTaxData(taxRes.data);
      const due = taxRes.data?.next_due_date ?? taxRes.data?.due_date ?? null;
      const qi = dueDateToQuarter(due);
      if (qi) {
        const payments = await getQuarterlyPayments(qi.tax_year);
        setPaidQuartersData(payments);
      }
    } catch {}
    // Marking a quarter paid/unpaid debits/credits the tax pool on the backend.
    await refetchSavingsAndInsights();
  }

  const negativeMonthInsight = insightsData.find(i => i.type === INSIGHT_TYPES.NEGATIVE_MONTH_MOVE) ?? null;
  const deductionInsight     = insightsData.find(i => i.type === INSIGHT_TYPES.DEDUCTION_RUNNING_TOTAL) ?? null;

  return {
    loading,
    taxData,
    expensesData,
    curveData,
    cashflowData,
    paidQuartersData,
    taxSetupData,
    insightsData,
    savingsData,
    savingsModalPool,
    setSavingsModalPool,
    refetchSavingsAndInsights,
    refetchTaxAndPayments,
    negativeMonthInsight,
    deductionInsight,
  };
}
